import { useQuery } from '@tanstack/react-query';
import { useQAPairAIReplyError, useQAPairManagerPermission } from './useQAPair';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';
import { eventBus } from 'services/eventBus';
import { queries } from 'services/queries';
import { isIdEqual } from 'utils/is';
import { uniq } from 'lodash-es';
import { SupplementKnowledgeValueMessage } from 'services/api/supplementKnowledge';

/**
 * 已提交过AI回复有误的queries
 */
const qaPairSubmitMsgIdsRecordState = atom<
  | {
      queryMsgIds: string[];
      answersMsgIds: string[];
    }
  | undefined
>(undefined);

const qaPairSubmitQueryMsgIdsRecordState = atom<string[]>(
  (get) => get(qaPairSubmitMsgIdsRecordState)?.queryMsgIds ?? [],
);

const qaPairSubmitAnswersMsgIdsRecordState = atom<string[]>(
  (get) => get(qaPairSubmitMsgIdsRecordState)?.answersMsgIds ?? [],
);

export const useQAPairSubmitQueryMsgIdsRecord = () =>
  useAtomValue(qaPairSubmitQueryMsgIdsRecordState);
export const useQAPairSubmitAnswersMsgIdsRecord = () =>
  useAtomValue(qaPairSubmitAnswersMsgIdsRecordState);

export const useSetQAPairSubmitMsgIdsRecord = () => useSetAtom(qaPairSubmitMsgIdsRecordState);

export const useQAPairRecord = (conversation: Conversation.Conversation) => {
  const { conversationId } = conversation;
  const aiReplyErrorPermission = useQAPairAIReplyError();
  const managerPermission = useQAPairManagerPermission();

  const setQAPairSubmitMsgIdsRecord = useSetQAPairSubmitMsgIdsRecord();

  const enabled = useMemo(
    () => (aiReplyErrorPermission || managerPermission) && !!conversationId,
    [aiReplyErrorPermission, conversationId, managerPermission],
  );
  /**
   * 返回当前会话已经在AI回复错误列表存在的case query
   */
  const { data, refetch: refetchQAPairReply } = useQuery({
    enabled,
    ...queries.qaPair.submitMsgIdsRecord(conversationId),
  });
  const queryReplyData = data?.data?.data;

  useEffect(() => {
    if (queryReplyData) {
      const queryMsgIds: string[] = [];
      const answersMsgIds: string[] = [];
      queryReplyData.forEach((item) => {
        if (item.queryMsgId) {
          queryMsgIds.push(`${item.queryMsgId}`);
        }
        item.queryMsgIds?.forEach((queryMsgId) => {
          queryMsgIds.push(`${queryMsgId}`);
        });
        item.answersMsgIds?.forEach((answerMsgId) => {
          answersMsgIds.push(`${answerMsgId}`);
        });
      });
      setQAPairSubmitMsgIdsRecord({
        queryMsgIds,
        answersMsgIds,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryReplyData]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handler = (_conversationId: string) => {
      if (isIdEqual(_conversationId, conversationId)) {
        refetchQAPairReply();
      }
    };

    eventBus.on('QAPAIR_RECORD_REFETCH', handler);
    return () => {
      eventBus.off('QAPAIR_RECORD_REFETCH', handler);
    };
  }, [enabled, conversationId, refetchQAPairReply]);

  return queryReplyData;
};

export const useUpdateQAPairRecord = () => {
  const setQAPairSubmitMsgIdsRecord = useSetAtom(qaPairSubmitMsgIdsRecordState);

  return useCallback(
    (
      queryList: SupplementKnowledgeValueMessage[],
      answersList: SupplementKnowledgeValueMessage[],
    ) => {
      const queryMsgIds = queryList.map((item) => item.messageId ?? '');
      const answersMsgIds = answersList.map((item) => item.messageId ?? '');
      setQAPairSubmitMsgIdsRecord((prev) => {
        if (!prev) {
          return { queryMsgIds, answersMsgIds };
        }
        return {
          queryMsgIds: uniq([...prev.queryMsgIds, ...queryMsgIds]),
          answersMsgIds: uniq([...prev.answersMsgIds, ...answersMsgIds]),
        };
      });
    },
    [setQAPairSubmitMsgIdsRecord],
  );
};
